import { SMART_FIELD_CLASSNAME } from '~/components/OneComposer/one-composer.consts';

/**
 * Sanitizes subject content by preserving smart fields while cleaning HTML
 */
export const sanitizeSubjectContent = ({
    editorData,
    isForUseInEditor
}: {
    editorData: string;
    isForUseInEditor?: boolean;
}) => {
    const { processedContent, smartFieldSpans } =
        extractSmartFieldSpans(editorData);
    const cleanedContent = cleanHtmlContent({
        content: processedContent,
        isForUseInEditor
    });
    const restoredContent = restoreSmartFieldSpans(
        cleanedContent,
        smartFieldSpans
    );
    return !!restoredContent && isForUseInEditor
        ? `<p>${restoredContent}</p>`
        : restoredContent;
};

/**
 * Extracts smartfield spans from content and replaces them with placeholders
 */
export const extractSmartFieldSpans = (content: string) => {
    const smartFieldRegex = new RegExp(
        `<span[^>]*class\\s*=\\s*["']?[^"']*\\b${SMART_FIELD_CLASSNAME}\\b[^"']*["']?[^>]*>.*?</span>`,
        'gi'
    );
    const matches = Array.from(content.matchAll(smartFieldRegex));
    const smartFieldSpans = matches.map(match => match[0]);
    const processedContent = matches.reduce((acc, match, index) => {
        const placeholder = `__SMARTFIELD_${index}__`;
        return acc.replace(match[0], placeholder);
    }, content);
    return { processedContent, smartFieldSpans };
};

/**
 * Removes HTML tags and normalizes whitespace from content
 */
export const cleanHtmlContent = ({
    content,
    isForUseInEditor
}: {
    content: string;
    isForUseInEditor?: boolean;
}): string => {
    let cleanedContent = content.replace(/<[^>]*>/g, ''); // Remove all HTML tags
    // Handle html entities; based on usage context - replace with actual characters when not for editor, preserve when for editor
    if (!isForUseInEditor) {
        cleanedContent = cleanedContent
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&'); // Decode &amp; last to avoid double-decoding
    }
    return cleanedContent;
};

/**
 * Restores smartfield spans from placeholders in the content
 */
export const restoreSmartFieldSpans = (
    content: string,
    smartFieldSpans: string[]
): string => {
    let restoredContent = content;
    smartFieldSpans.forEach((span, index) => {
        const placeholder = `__SMARTFIELD_${index}__`;
        restoredContent = restoredContent.replace(placeholder, span);
    });
    return restoredContent;
};
